import {
  extractValueFromCookie,
  saveCookie,
  storeIntoLocal,
  saveUserData,
} from "./helper-data";
import { getAccessToken } from "./session";
import ApiCalls from "../../api/customAxios";
import { enterpriseUserUrl } from "../../config/service-urls";

export const ADMIN_USER_TYPES = ["Super Admin", "Program Manager"];
export const ENTERPRISE_USER_INFO_URL = `${enterpriseUserUrl}/info`;

export const hasAuthSession = () => Boolean(getAccessToken());

export const getStoredEnterpriseId = () =>
  localStorage.getItem("enterprise_id") ||
  extractValueFromCookie("enterprise_id", document.cookie) ||
  null;

export const hasEnterpriseIdInUrl = (searchParams) =>
  Boolean(searchParams?.get?.("enterprise_id"));

export const getEffectiveEnterpriseId = (searchParams) =>
  searchParams?.get?.("enterprise_id") || getStoredEnterpriseId() || null;

export const canBootstrapCareerPortal = (searchParams) =>
  Boolean(getEffectiveEnterpriseId(searchParams));

export const shouldEnterCareerPortal = (searchParams) => {
  if (!hasAuthSession()) {
    return false;
  }
  consumeEnterpriseIdFromQuery(searchParams);
  return canBootstrapCareerPortal(searchParams);
};

export const clearEnterpriseHandoffState = () => {
  localStorage.removeItem("enterprise_id");
  localStorage.removeItem("client_name");
  document.cookie =
    "enterprise_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  clearEnterpriseInfoRequestCache();
};

export const clearStaleEnterpriseBootstrap = () => {
  localStorage.removeItem("client_name");
  localStorage.removeItem("enterprise_name");
  localStorage.removeItem("profile_image");
  localStorage.removeItem("short_logo");
  localStorage.removeItem("logo");
  localStorage.removeItem("mspEnterprsieName");
  localStorage.removeItem("childEnterpriseId");
  localStorage.removeItem("MSPName");
  localStorage.removeItem("validDomainNames");
  localStorage.removeItem("encoded_domain");
  localStorage.removeItem("profileTitle");
};

export const redirectToPublicHomeForMissingEnterprise = () => {
  clearEnterpriseHandoffState();
  window.location.replace("/");
};

export const buildCandidateRoute = (path, searchParams) => {
  if (!getEffectiveEnterpriseId(searchParams)) {
    return null;
  }
  const basePath = path.split("?")[0];
  const params = new URLSearchParams(searchParams?.toString?.() ?? "");
  params.delete("enterprise_id");
  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
};

export const navigateToCandidateRoute = (navigate, path, searchParams) => {
  const route = buildCandidateRoute(path, searchParams);
  if (!route) {
    redirectToPublicHomeForMissingEnterprise();
    return;
  }
  navigate(route);
};

export const applyEnterpriseIdFromQuery = (searchParams) => {
  const enterpriseId = searchParams?.get?.("enterprise_id");
  if (!enterpriseId) {
    return null;
  }

  const previousId =
    localStorage.getItem("client_name") || getStoredEnterpriseId();
  if (previousId && previousId !== enterpriseId) {
    clearStaleEnterpriseBootstrap();
    clearEnterpriseInfoRequestCache();
  } else {
    localStorage.removeItem("client_name");
    clearEnterpriseInfoRequestCache();
  }

  persistEnterpriseId(enterpriseId);
  return enterpriseId;
};

export const stripEnterpriseIdFromUrl = (searchParams) => {
  if (!searchParams?.get?.("enterprise_id")) {
    return;
  }
  const nextParams = new URLSearchParams(searchParams.toString());
  nextParams.delete("enterprise_id");
  const qs = nextParams.toString();
  window.history.replaceState(
    {},
    document.title,
    `${window.location.pathname}${qs ? `?${qs}` : ""}`,
  );
};

export const consumeEnterpriseIdFromQuery = (searchParams) => {
  const fromUrl = applyEnterpriseIdFromQuery(searchParams);
  if (fromUrl) {
    stripEnterpriseIdFromUrl(searchParams);
    return fromUrl;
  }
  return getStoredEnterpriseId();
};

export const persistEnterpriseId = (enterpriseId) => {
  if (!enterpriseId) {
    return;
  }
  localStorage.setItem("enterprise_id", enterpriseId);
  saveCookie("enterprise_id", enterpriseId, 365);
};

export const resolveAppOrigin = (searchParams) => {
  if (searchParams?.get?.("appOrigin")) {
    return searchParams.get("appOrigin");
  }
  return process.env.REACT_APP_ORIGIN === "neurodiversify" ? "N" : "D";
};

export const findEnterpriseUser = (users, enterpriseId) => {
  if (!users?.length || !enterpriseId) {
    return undefined;
  }
  return users.find((user) => user.enterprise_id === enterpriseId);
};

export const resolveEnterpriseUserFromInfo = (users, enterpriseId) => {
  if (!users?.length || !enterpriseId) {
    return undefined;
  }
  return findEnterpriseUser(users, enterpriseId);
};

export const isAdminUser = (userType) => ADMIN_USER_TYPES.includes(userType);

let enterpriseInfoRequest = null;
let enterpriseInfoRequestId = null;

export const clearEnterpriseInfoRequestCache = () => {
  enterpriseInfoRequest = null;
  enterpriseInfoRequestId = null;
};

export const fetchEnterpriseUserInfo = (enterpriseId) => {
  if (!enterpriseId) {
    return Promise.reject(new Error("Missing enterprise id"));
  }

  if (enterpriseInfoRequest && enterpriseInfoRequestId === enterpriseId) {
    return enterpriseInfoRequest;
  }

  enterpriseInfoRequestId = enterpriseId;
  enterpriseInfoRequest = ApiCalls.get(ENTERPRISE_USER_INFO_URL)
    .then((response) => {
      const user = resolveEnterpriseUserFromInfo(response?.user, enterpriseId);
      if (!user) {
        clearEnterpriseInfoRequestCache();
        return Promise.reject(new Error("Enterprise user not found"));
      }
      return user;
    })
    .catch((error) => {
      clearEnterpriseInfoRequestCache();
      return Promise.reject(error);
    });

  return enterpriseInfoRequest;
};

export const redirectToAccessDenied = (navigate, searchParams) => {
  const route = buildCandidateRoute("/candidate/access-denied", searchParams);
  navigate(route ?? "/candidate/access-denied");
};

export const handleEnterpriseInfoError = (error) => {
  if (error) {
    console.error("Enterprise /info bootstrap failed", error);
  }
  clearEnterpriseInfoRequestCache();
  clearEnterpriseHandoffState();
  window.location.replace("/");
};

export const persistEnterpriseBootstrap = (enterpriseUser, appOrigin) => {
  if (!enterpriseUser) {
    return;
  }
  localStorage.setItem("client_name", enterpriseUser.enterprise_id);
  persistEnterpriseId(enterpriseUser.enterprise_id);
  storeIntoLocal(enterpriseUser);
  localStorage.removeItem("childEnterpriseId");
  localStorage.removeItem("MSPName");
  saveUserData(enterpriseUser);
  localStorage.setItem(
    "mspEnterprsieName",
    enterpriseUser.establishment_name ?? "",
  );
  localStorage.setItem("appOrigin", appOrigin);
};

export const setFaviconForOrigin = () => {
  const href =
    process.env.REACT_APP_ORIGIN === "neurodiversify"
      ? "/neuro-favicon.png"
      : "/favicon.png";
  document.querySelector("link[title=icon]")?.setAttribute("href", href);
};
